import { useState, useRef, useCallback } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import { useMediaDB } from "@/hooks/useMediaDB";
import { useAdminI18n } from "@/hooks/useAdminI18n";
import type { MediaItem } from "@/mocks/admin";

interface MediaGridProps {
  items: MediaItem[];
  selectable?: boolean;
  selectedId?: string | null;
  onSelect?: (item: MediaItem) => void;
  onCopy: (id: string, url: string) => void;
  onDelete: (id: string) => void;
  copiedId: string | null;
}

function MediaGrid({ items, selectable, selectedId, onSelect, onCopy, onDelete, copiedId }: MediaGridProps) {
  if (items.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-lg border border-hj-gray-border">
        <i className="ri-image-line text-4xl text-hj-gray-border mb-3 block"></i>
        <p className="text-hj-gray text-sm">Keine Medien gefunden.</p>
        <p className="text-xs text-hj-gray mt-1">Laden Sie Bilder hoch, um sie hier zu sehen.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {items.map((item) => {
        const isSelected = selectedId === item.id;
        return (
          <div
            key={item.id}
            onClick={() => selectable && onSelect?.(item)}
            className={`bg-white rounded-lg border overflow-hidden group transition-all ${
              selectable ? "cursor-pointer" : ""
            } ${
              isSelected
                ? "border-hj-red ring-2 ring-hj-red/30"
                : "border-hj-gray-border hover:border-hj-gray"
            }`}
          >
            <div className="aspect-square bg-hj-gray-light relative">
              {item.type === "video" ? (
                <div className="w-full h-full flex items-center justify-center bg-hj-dark">
                  <i className="ri-play-circle-line text-4xl text-white/60"></i>
                </div>
              ) : (
                <img
                  src={item.url}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}

              {/* Selected overlay */}
              {isSelected && (
                <div className="absolute inset-0 bg-hj-red/20 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-hj-red flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                </div>
              )}

              {/* Hover actions (only when not in select mode) */}
              {!selectable && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); onCopy(item.id, item.url); }}
                      className="w-8 h-8 rounded-full bg-white flex items-center justify-center cursor-pointer"
                      title="URL kopieren"
                    >
                      <i className={`${copiedId === item.id ? "ri-check-line text-emerald-500" : "ri-link text-hj-dark"} text-sm`}></i>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                      className="w-8 h-8 rounded-full bg-white flex items-center justify-center cursor-pointer"
                      title="Löschen"
                    >
                      <i className="ri-delete-bin-line text-sm text-red-500"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="p-2.5">
              <p className="text-xs font-medium text-hj-dark truncate">{item.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-hj-gray">{item.size}</span>
                {item.dimensions && <span className="text-[10px] text-hj-gray">{item.dimensions}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminMedia() {
  const { media, loading, error: dbError, uploadProgress, uploadMedia, deleteMedia } = useMediaDB();
  const { t } = useAdminI18n();
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = media.filter((m) => {
    const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase());
    const matchesType = !filterType || m.type === filterType;
    return matchesSearch && matchesType;
  });

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArr = Array.from(files);
    if (fileArr.length === 0) return;
    setUploadErrors([]);

    const errors: string[] = [];
    for (const file of fileArr) {
      if (file.size > 20 * 1024 * 1024) {
        errors.push(`${file.name}: Datei zu groß (max. 20 MB)`);
        continue;
      }
      const { error } = await uploadMedia(file);
      if (error) errors.push(`${file.name}: ${error}`);
    }
    if (errors.length > 0) setUploadErrors(errors);
  }, [uploadMedia]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(e.target.files);
    e.target.value = "";
  }, [handleFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleCopyUrl = useCallback((id: string, url: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    await deleteMedia(id);
  }, [deleteMedia]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-hj-dark">{t("media.title")}</h1>
            <p className="text-sm text-hj-gray mt-0.5">
              {loading ? t("common.loading") : `${media.length} ${t("media.header.subtitle")}`}
            </p>
          </div>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploadProgress.uploading}
            className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors flex items-center gap-2 disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-upload-cloud-line"></i>
            {t("media.upload.button")}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={handleInputChange}
          />
        </div>

        {/* Upload Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploadProgress.uploading && inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition-colors ${
            uploadProgress.uploading
              ? "border-hj-red bg-hj-red/5 cursor-default"
              : dragOver
              ? "border-hj-red bg-hj-red/5 cursor-pointer"
              : "border-hj-gray-border hover:border-hj-gray cursor-pointer"
          }`}
        >
          {uploadProgress.uploading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm font-medium text-hj-red">
                {t("media.dropzone.uploading")}: {uploadProgress.fileName}
              </p>
              <div className="w-48 h-1.5 bg-hj-gray-light rounded-full overflow-hidden">
                <div
                  className="h-full bg-hj-red rounded-full transition-all"
                  style={{ width: `${uploadProgress.progress}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <>
              <i className="ri-image-add-line text-4xl text-hj-gray mb-2 block"></i>
              <p className="text-sm text-hj-gray">{t("media.dropzone.title")}</p>
              <p className="text-xs text-hj-gray mt-1">{t("media.dropzone.hint")}</p>
            </>
          )}
        </div>

        {/* Upload errors */}
        {uploadErrors.length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line text-red-600 mt-0.5 shrink-0"></i>
              <div>
                <p className="text-sm font-medium text-red-700 mb-1">{t("media.error.title")}:</p>
                {uploadErrors.map((e, i) => (
                  <p key={i} className="text-xs text-red-600">{e}</p>
                ))}
              </div>
              <button onClick={() => setUploadErrors([])} className="ml-auto shrink-0 text-red-400 hover:text-red-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* DB error */}
        {dbError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            <strong>Supabase-Fehler:</strong> {dbError}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("media.filter.placeholder")}
              className="w-full pl-9 pr-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white cursor-pointer"
          >
            <option value="">{t("media.filter.allTypes")}</option>
            <option value="image">{t("media.filter.images")}</option>
            <option value="video">{t("media.filter.videos")}</option>
          </select>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Grid */}
        {!loading && (
          <MediaGrid
            items={filtered}
            onCopy={handleCopyUrl}
            onDelete={handleDelete}
            copiedId={copiedId}
          />
        )}
      </div>
    </AdminLayout>
  );
}
